import { useCallback, useEffect, useMemo, useState } from "react";

import {
  addEntry as addEntryService,
  deleteEntry,
  subscribeToEntries,
  updateEntry as updateEntryService,
} from "../services/entry-service";

export function useEntries(activeGroup, user) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeGroup?.id) {
      setEntries([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeToEntries(activeGroup.id, (data) => {
      setEntries(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [activeGroup?.id]);

  const addEntry = useCallback(
    async (draft) => {
      if (!draft.title.trim() || !user || !activeGroup?.id) return;

      await addEntryService(activeGroup.id, {
        type: draft.type,
        title: draft.title.trim().slice(0, 200),
        addedBy: {
          uid: user.uid,
          email: user.email,
        },
        rating: draft.rating,
        status: draft.status,
        tags: draft.tags
          .split(",")
          .map((tag) => tag.trim().slice(0, 30))
          .filter(Boolean)
          .slice(0, 20),
        note: draft.note.trim().slice(0, 2000),
        studio: draft.studio.trim(),
        episodes: draft.episodes.trim(),
        director: draft.director.trim(),
        runtime: draft.runtime.trim(),
        artist: draft.artist.trim(),
        album: draft.album.trim(),
        author: draft.author.trim(),
        pages: draft.pages.trim(),
      });
    },
    [activeGroup?.id, user],
  );

  const updateEntry = useCallback(
    async (draft) => {
      if (!draft.id || !draft.title.trim() || !activeGroup?.id) return;

      await updateEntryService(activeGroup.id, draft.id, {
        type: draft.type,
        title: draft.title.trim().slice(0, 200),
        rating: draft.rating,
        status: draft.status,
        tags: draft.tags
          .split(",")
          .map((tag) => tag.trim().slice(0, 30))
          .filter(Boolean)
          .slice(0, 20),
        note: draft.note.trim().slice(0, 2000),
        studio: draft.studio.trim(),
        episodes: draft.episodes.trim(),
        director: draft.director.trim(),
        runtime: draft.runtime.trim(),
        artist: draft.artist.trim(),
        album: draft.album.trim(),
        author: draft.author.trim(),
        pages: draft.pages.trim(),
      });
    },
    [activeGroup?.id],
  );

  const removeEntry = useCallback(
    async (entryId) => {
      if (!activeGroup?.id) return;

      await deleteEntry(activeGroup.id, entryId);
    },
    [activeGroup?.id],
  );

  return useMemo(
    () => ({
      entries,
      loading,
      addEntry,
      updateEntry,
      removeEntry,
    }),
    [entries, loading, addEntry, updateEntry, removeEntry],
  );
}

export default useEntries;
